import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format, addDays } from "date-fns";
import { useAuth } from "@/hooks/useAuth";
import { fetchCustomerInfoBatch, CustomerInfo, CustomerInfoRow } from "@/lib/my-loads-helpers";
import { queryKeys } from "@/lib/query-keys";


export function useMyLoads() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Hae käyttäjän kuormat
  const { data: loads, isLoading } = useQuery({
    queryKey: [...queryKeys.myLoads.all, user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from("installation_loads")
        .select("*")
        .eq("installer_id", user.id)
        .is("deleted_at", null)
        .gte("load_date", format(addDays(new Date(), -7), "yyyy-MM-dd"))
        .order("load_date", { ascending: true });

      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
    gcTime: 1000 * 60 * 60 * 24,   // 24h - säilyy IndexedDB:ssä
  });

  // Hae asennukset kuormille + asiakastiedot
  const { data: installationsMap } = useQuery({
    queryKey: [...queryKeys.myLoads.installations(), loads?.map(l => l.id)],
    queryFn: async () => {
      if (!loads || loads.length === 0) return {};
      
      const { data, error } = await supabase
        .from("installations")
        .select(`
          id,
          load_id,
          load_position,
          order_id,
          cemetery_id,
          installer_id,
          status,
          task_type,
          scheduled_date,
          completed_date,
          notes,
          latitude,
          longitude,
          updated_at,
          created_at,
          deleted_at,
          cemetery_approval_status,
          cemetery_approval_token,
          cemetery_approval_requested_at,
          cemetery_approval_responded_at,
          cemetery_approval_notes,
          order:installer_orders_view(id, order_number, order_type),
          cemetery:cemeteries(name, city, address, latitude, longitude)
        `)
        .in("load_id", loads.map(l => l.id))
        .is("deleted_at", null)
        .order("load_position", { ascending: true });

      if (error) throw error;

      // Hae asiakastiedot
      const orderIds = [...new Set(data?.map(inst => inst.order?.id).filter(Boolean) as string[])];
      const customerInfoMap = await fetchCustomerInfoBatch(orderIds);

      // Yhdistä asiakastiedot asennuksiin
      const enhancedData = data?.map(inst => ({
        ...inst,
        order: inst.order ? {
          ...inst.order,
          customer: inst.order.id ? customerInfoMap[inst.order.id] || null : null
        } : null
      }));

      // Ryhmittele load_id:n mukaan
      const map: Record<string, typeof enhancedData> = {};
      enhancedData?.forEach(inst => {
        if (inst.load_id) {
          if (!map[inst.load_id]) map[inst.load_id] = [];
          map[inst.load_id].push(inst);
        }
      });
      return map;
    },
    enabled: !!loads && loads.length > 0,
    gcTime: 1000 * 60 * 60 * 24,   // 24h offline-tuki
  });

  return {
    loads: loads || [],
    isLoading,
    installationsMap: installationsMap || {},
    queryClient,
  };
}
